import clsx from "clsx";
import { useMemo } from "react";
import { AccountItem } from "./AccountItem";
import { NormalizedAccount } from "../state";
import { ACCOUNT_LIST, CATEGORY_METADATA } from "../labeling.constants";
import { AccountCategory } from "@/entities/DataAssociation/Account.model";

export function AccountList({
    accounts,
    recommendedIds = [],
    selectedId,
    onAccountSelection,
}: {
    accounts: NormalizedAccount[];
    recommendedIds?: string[];
    selectedId?: string;
    onAccountSelection?: (account: NormalizedAccount) => void;
}) {
    const { groupedAccounts, categories, hotkeyMap } = useMemo(() => {
        const groups: Record<AccountCategory, NormalizedAccount[]> =
            {} as Record<AccountCategory, NormalizedAccount[]>;
        accounts.forEach((account) => {
            const category = ACCOUNT_LIST[account.label]?.category;
            if (!category) return;
            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(account);
        });

        const cats = Object.keys(groups) as AccountCategory[];
        const hotkeys: Record<string, number> = {};
        let globalIndex = 0;
        for (const cat of cats) {
            for (const account of groups[cat]) {
                if (globalIndex < 3) {
                    hotkeys[account.id] = globalIndex + 1;
                }
                globalIndex++;
            }
        }

        return {
            groupedAccounts: groups,
            categories: cats,
            hotkeyMap: hotkeys,
        };
    }, [accounts]);

    return (
        <div
            className={clsx(
                "pb-4",
                "hide-scrollbar flex flex-1 flex-col gap-4 overflow-y-auto",
            )}
        >
            {categories.map((category) => (
                <div key={category}>
                    <div
                        className={clsx(
                            "lozenge",
                            "flex items-center gap-4 px-3 py-4 font-medium",
                        )}
                    >
                        {CATEGORY_METADATA[category]?.name || category}
                    </div>
                    <ul role="list" className="flex flex-col gap-1">
                        {groupedAccounts[category].map((account) => (
                            <AccountItem
                                key={account.id}
                                account={account}
                                recommended={recommendedIds.includes(
                                    account.id,
                                )}
                                selected={selectedId === account.id}
                                onAccountSelection={onAccountSelection}
                                hotkeyNumber={hotkeyMap[account.id]}
                            />
                        ))}
                    </ul>
                </div>
            ))}
            {accounts.length === 0 && (
                <p className="text-subdued body-s text-center">
                    No accounts exist for this category.
                </p>
            )}
        </div>
    );
}
